/* eslint-disable react-hooks/exhaustive-deps */
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { DataWindow, useDataWindows } from "../../api/useMetaDataApi";
import { usePayerLandscapeDetailDropdowns } from "../../api/usePayerLandscapeDetailApi";
import { ReactComponent as CompareIcon } from "../../assets/CompareIcon.svg";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import GeoSelect from "../../components/GeoSelect/GeoSelect";
import PageHeader from "../../components/Headers/PageHeader";
import MinimalSelect from "../../components/Inputs/MinimalSelect";
import Select from "../../components/Inputs/Select";
import { useModal } from "../../components/Modal/useModal";
import SearchBox from "../../components/SearchBox/SearchBox";
import { Tab, TabPanel, useTabs } from "../../components/Tabs";
import Tooltip from "../../components/Tooltip/Tooltip";
import {
  useClientConfiguration,
  useClientIndicationFiltersconfiguration,
  useClientPayerLandscapeConfiguration,
} from "../../data/global/useProductConfiguration";
import { usePayerLandscapeDispatch, usePayerLandscapeState } from "../../data/payerLandscape/hooks";
import useGetRole, { FRM_ROLE } from "../../hooks/useGetRole";
import { useProducts } from "../../hooks/useProducts";
import useQueryParams from "../../hooks/useQueryParams";
import { arrayToOptions } from "../../utils/arrayToOptions";
import DetailFiltersModal from "./DetailFiltersModal";
import PayerLandscapeComparsionTab from "./PayerLandscapeComparisonTab";
import PayerLandscapeDetailTab from "./PayerLandscapeDetailTab";
import ProductComparisonFiltersModal from "./ProductComparisonFiltersModal";

enum TabNames {
  DETAIL_VIEW = "detailed-view",
  PRODUCT_COMPARISON = "product-comparison",
}

const PayerLandscape: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setModal } = useModal();
  const [initTab] = useQueryParams("tab", TabNames.DETAIL_VIEW);
  const tabs = useTabs(initTab);

  const { currentTab, changeTab } = tabs;
  const { sharedFilters, detailFilters, detailFilterCount, comparisonFilterCount } = usePayerLandscapeState();

  const { updateSharedFilters, updateDetailFilters } = usePayerLandscapeDispatch();
  const { client: selectedClient } = useParams() as { client: string };
  const { productComparison: showproductComparisonParam, indicationFiltering: showIndicationFiltering } =
    useClientPayerLandscapeConfiguration(selectedClient);
  const { products: allowedPlanInfoProducts } = useClientConfiguration(selectedClient);
  const { data: role } = useGetRole(selectedClient);
  const isFRM = String(role) === FRM_ROLE;
  const [search, setSearch] = useState<string>(sharedFilters.planName ?? "");

  useEffect(() => {
    // Keep input in sync when planName is cleared/changed outside this component (e.g., clear filters).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearch(sharedFilters.planName ?? "");
  }, [sharedFilters.planName]);

  const dropdownQuery = usePayerLandscapeDetailDropdowns(selectedClient);
  const products = useProducts(selectedClient);
  const dropdowns = dropdownQuery.data;
  const allProducts = products.data || [];

  const previousIndicationRef = useRef<string | null | undefined>(detailFilters.indication);

  // Memoize this calculation to prevent it from running on every render
  const planInfoProducts = useMemo(() => {
    let filteredProducts =
      !allowedPlanInfoProducts || allowedPlanInfoProducts.length === 0
        ? allProducts
        : allProducts.filter((product) => allowedPlanInfoProducts.includes(product));

    // Filter by indication if one is selected and we have the mapping
    if (detailFilters.indication && dropdowns?.productIndicationMap) {
      const indicationKey = detailFilters.indication || "All";
      const productsForIndication = dropdowns.productIndicationMap[indicationKey];

      if (productsForIndication && productsForIndication.length > 0) {
        // Keep only products that exist in both lists
        filteredProducts = filteredProducts.filter((product) => productsForIndication.includes(product));
      }
    }

    // Remove duplicates (your existing logic)
    return Array.from(new Set(filteredProducts));
  }, [allowedPlanInfoProducts, allProducts, detailFilters.indication, dropdowns?.productIndicationMap]);

  // Memoize product options to prevent recreating on every render
  const productOptions = useMemo(() => arrayToOptions(planInfoProducts, false), [planInfoProducts]);

  // Use memoized config
  const config = useClientConfiguration(selectedClient);

  // Derived: product dropdown is disabled only for BRAFTOVI under All Indications with indication filtering
  const isProductDropdownDisabled =
    selectedClient === "BRAFTOVI" && detailFilters.indication === "All Indications" && !!showIndicationFiltering;

  // Derived: show the special single-product options list when BRAFTOVI All Indications is active
  const displayProductOptions = useMemo(() => {
    if (selectedClient === "BRAFTOVI" && detailFilters.indication === "All Indications" && showIndicationFiltering) {
      return [{ label: "BRAFTOVI", value: "BRAFTOVI+MEKTOVI (Melanoma)" }];
    }
    return productOptions;
  }, [selectedClient, detailFilters.indication, showIndicationFiltering, productOptions]);

  // Derived: product comparison tab visibility (BRAFTOVI hides it for All Indications / mCRC)
  const showProductComparison = useMemo(() => {
    if (selectedClient === "BRAFTOVI" && showIndicationFiltering && !isFRM) {
      const shouldHide = detailFilters.indication === "All Indications" || detailFilters.indication === "mCRC";
      return shouldHide ? false : showproductComparisonParam;
    }
    return showproductComparisonParam;
  }, [selectedClient, showIndicationFiltering, isFRM, detailFilters.indication, showproductComparisonParam]);

  // Side-effect: switch away from product comparison tab when it becomes hidden
  useEffect(() => {
    if (
      selectedClient === "BRAFTOVI" &&
      showIndicationFiltering &&
      !isFRM &&
      (detailFilters.indication === "All Indications" || detailFilters.indication === "mCRC") &&
      currentTab === TabNames.PRODUCT_COMPARISON
    ) {
      changeTab(TabNames.DETAIL_VIEW);
    }
  }, [selectedClient, showIndicationFiltering, isFRM, detailFilters.indication, currentTab, changeTab]);

  useEffect(() => {
    // When indication changes, check if current product is still valid for the new list
    if (
      detailFilters.indication &&
      detailFilters.product &&
      planInfoProducts.length > 0 &&
      !planInfoProducts.includes(detailFilters.product)
    ) {
      // Current product is not valid for this indication, reset to first available
      updateDetailFilters({ product: planInfoProducts[0] });
    }
  }, [detailFilters.indication, detailFilters.product, planInfoProducts, updateDetailFilters]);

  useEffect(() => {
    // Only for BRAFTOVI: force the correct product when All Indications is selected
    if (
      !config?.payerLandscape ||
      !productOptions.length ||
      selectedClient !== "BRAFTOVI" ||
      detailFilters.indication !== "All Indications" ||
      !showIndicationFiltering ||
      detailFilters.product === "BRAFTOVI+MEKTOVI (Melanoma)"
    ) {
      return;
    }
    updateDetailFilters({ product: "BRAFTOVI+MEKTOVI (Melanoma)" });
  }, [
    config,
    productOptions.length,
    selectedClient,
    detailFilters.indication,
    detailFilters.product,
    showIndicationFiltering,
    updateDetailFilters,
  ]);

  // Clear filters when indication changes (since available columns/filters change)
  useEffect(() => {
    const currentIndication = detailFilters.indication;
    const previousIndication = previousIndicationRef.current;

    // Only clear filters if indication actually changed (not on initial load)
    if (previousIndication !== undefined && currentIndication !== previousIndication) {
      // Clear detail filters that might not be relevant to the new indication
      updateDetailFilters({
        indication: currentIndication,
        product: detailFilters.product,
        // Keep lives filters as they're always visible
        minimumLives: detailFilters.minimumLives,
        maximumLives: detailFilters.maximumLives,
        rangeTypeLives: detailFilters.rangeTypeLives,
        // Reset all other filters
        coverageStatus: undefined,
        compCcStatus: undefined,
        relativeStatus: undefined,
        minimumTrx: undefined,
        maximumTrx: undefined,
        rangeTypeTrx: undefined,
        minimumMarketTrx: undefined,
        maximumMarketTrx: undefined,
        rangeTypeMarketTrx: undefined,
        minimumMarketShare: undefined,
        maximumMarketShare: undefined,
        rangeTypeMarketShare: undefined,
        minimumMonthlyDelta: undefined,
        maximumMonthlyDelta: undefined,
        rangeTypeMonthlyDelta: undefined,
      });
    }

    // Update the ref for next comparison
    previousIndicationRef.current = currentIndication;
  }, [detailFilters.indication]);

  const beneOptions = useMemo(
    () => arrayToOptions(dropdowns?.benificiaryType, false, t("common.allChannels")),
    [dropdowns?.benificiaryType, t],
  );

  const shouldShowTooltip = useMemo(
    () => i18n.exists(`payerLandscape.tooltip.${selectedClient}`) || isFRM,
    [i18n, selectedClient, isFRM],
  );

  const tooltipContent = useMemo(
    () =>
      isFRM
        ? i18n.exists(`payerLandscape.frmTooltip.${selectedClient}`)
          ? (t(`payerLandscape.frmTooltip.${selectedClient}`) as string)
          : (t(`payerLandscape.frmTooltip.default`) as string)
        : (t(`payerLandscape.tooltip.${selectedClient}`) as string),
    [isFRM, t, selectedClient, i18n],
  );

  const advancedFilterCount = currentTab === TabNames.DETAIL_VIEW ? detailFilterCount : comparisonFilterCount;
  const { data: dataWindows } = useDataWindows(selectedClient);

  const timePeriodOptions = useMemo(
    () =>
      (dataWindows || []).map((dataWindow) => ({
        label: t(`payerLandscape.timePeriodOptions.${dataWindow}`),
        value: dataWindow,
      })),
    [dataWindows, t],
  );

  const defaultDataWindow = useMemo(
    () => (dataWindows || []).find((dataWindow) => dataWindow === DataWindow.twelveMonths) || DataWindow.twelveMonths,
    [dataWindows],
  );

  const showTimePeriodSelection = (dataWindows || []).length > 1 && currentTab === TabNames.DETAIL_VIEW;

  const indicationOptions = useClientIndicationFiltersconfiguration(selectedClient);

  //IF INDICATION SET, BUT SHOULDNT BE, SET TO UNDEFINED
  useEffect(() => {
    if (!showIndicationFiltering && detailFilters.indication !== undefined) {
      updateDetailFilters({ indication: undefined });
    }
  }, [updateDetailFilters, showIndicationFiltering, detailFilters.indication]);

  //IF NO INDICATION SET, SET TO FIRST PRODUCT AVAILABLE
  useEffect(() => {
    if (
      indicationOptions &&
      showIndicationFiltering &&
      indicationOptions.length > 0 &&
      detailFilters.indication === undefined
    ) {
      updateDetailFilters({ indication: indicationOptions[0].value });
    }
  }, [updateDetailFilters, indicationOptions, detailFilters.indication, showIndicationFiltering]);

  //IF NO PRODUCT SET, SET TO FIRST PRODUCT AVAILABLE
  useEffect(() => {
    if (planInfoProducts.length > 0 && detailFilters.product === "") {
      updateDetailFilters({ product: planInfoProducts[0] });
    }
  }, [updateDetailFilters, planInfoProducts, detailFilters.product]);

  // Effect to reset products when leaving All Indications for BRAFTOVI client
  useEffect(() => {
    if (
      selectedClient === "BRAFTOVI" &&
      detailFilters.indication !== "All Indications" &&
      planInfoProducts.length > 0 &&
      detailFilters.product &&
      !planInfoProducts.includes(detailFilters.product)
    ) {
      // Reset to first available product
      updateDetailFilters({ product: planInfoProducts[0] });
    }
  }, [detailFilters.indication, detailFilters.product, updateDetailFilters, planInfoProducts, selectedClient]);

  //Set the default beneficiaryType to first option (Commerical)
  useEffect(() => {
    if (!sharedFilters.beneficiaryType && beneOptions.length > 1) {
      updateSharedFilters({ beneficiaryType: beneOptions[0].value });
    }
  }, [beneOptions, sharedFilters.beneficiaryType, updateSharedFilters]);

  //Set the default timePeriod to last 12 months
  useEffect(() => {
    if (!sharedFilters.timePeriod && (dataWindows || []).length > 0) {
      updateSharedFilters({ timePeriod: defaultDataWindow });
    }
  }, [dataWindows, defaultDataWindow, sharedFilters.timePeriod, updateSharedFilters]);

  //IF BENETYPE IS INVALID, SET TO FIRST VALID OPTION
  useEffect(() => {
    if (!dropdowns?.benificiaryType) {
      return;
    }
    if (
      !dropdowns?.benificiaryType?.includes(sharedFilters.beneficiaryType || "") &&
      dropdowns?.benificiaryType.length > 0
    ) {
      updateSharedFilters({
        beneficiaryType: dropdowns?.benificiaryType[0],
      });
    }
  }, [dropdowns?.benificiaryType, sharedFilters.beneficiaryType, updateSharedFilters]);

  //Debounce search and update filters
  useEffect(() => {
    const timeoutAmount = search === "" ? 0 : 400;
    const timeoutId = setTimeout(() => {
      updateSharedFilters({ planName: search });
    }, timeoutAmount);

    //If a new keystroke happens before timeout, clear the timeout
    return () => {
      clearTimeout(timeoutId);
    };
  }, [search, updateSharedFilters]);

  const openFilterModal = () => {
    if (currentTab === TabNames.DETAIL_VIEW) {
      setModal(<DetailFiltersModal client={selectedClient} product={detailFilters.product} />);
    }
    if (currentTab === TabNames.PRODUCT_COMPARISON) {
      setModal(<ProductComparisonFiltersModal client={selectedClient} />);
    }
  };

  return (
    <>
      <PageHeader title={t("common.payerLandscape")}>
        <SearchBox value={search} handleChange={setSearch} />
      </PageHeader>

      <BreadCrumbs />

      <div>
        <div className="md:flex md:space-x-10  justify-between">
          <div
            className={
              currentTab === TabNames.DETAIL_VIEW &&
              indicationOptions &&
              showIndicationFiltering &&
              !(isFRM && selectedClient == "BRAFTOVI")
                ? "flex justify-evenly lg:w-6/12"
                : "flex justify-evenly lg:w-4/12"
            }
          >
            {currentTab === TabNames.DETAIL_VIEW && (
              <MinimalSelect
                value={detailFilters.product}
                handleChange={(value) => updateDetailFilters({ product: value })}
                label={t("payerLandscape.planInfoFor.default")}
                options={displayProductOptions}
                disabled={isProductDropdownDisabled}
              />
            )}
            {indicationOptions && showIndicationFiltering && !(isFRM && selectedClient == "BRAFTOVI") && (
              <MinimalSelect
                value={detailFilters.indication}
                handleChange={(value) => updateDetailFilters({ indication: value })}
                label={
                  i18n.exists(`payerLandscape.indicationFor.${selectedClient}`)
                    ? t(`payerLandscape.indicationFor.${selectedClient}`)
                    : t("payerLandscape.indicationFor.default")
                }
                options={indicationOptions}
              />
            )}
          </div>

          {!isFRM && (
            <div className={`${!isFRM ? "lg:w-3/12" : "lg:w-6/12"} grow`}>
              {showTimePeriodSelection ? (
                <Select
                  value={sharedFilters.timePeriod}
                  label={t("payerLandscape.timePeriod")}
                  options={timePeriodOptions}
                  handleChange={(value) => updateSharedFilters({ timePeriod: value })}
                />
              ) : null}
            </div>
          )}
          <div className={`${!isFRM ? "lg:w-7/12" : "lg:w-6/12"} grow lg:grow-0 md:flex md:space-x-10`}>
            <GeoSelect client={selectedClient} />
            <Select
              value={sharedFilters.beneficiaryType}
              handleChange={(value) => updateSharedFilters({ beneficiaryType: value })}
              label={
                i18n.exists(`payerLandscape.beneType.${selectedClient}`)
                  ? t(`payerLandscape.beneType.${selectedClient}`)
                  : t("payerLandscape.beneType.default")
              }
              options={beneOptions}
            />
          </div>
        </div>
        <div className="flex mb-4 md:justify-end">
          <button
            onClick={openFilterModal}
            className="text-primary w-full md:w-auto font-bold py-3 px-2 md:py-1 md:ml-0 md:-mr-2 rounded"
          >
            {t("common.advancedFilterWithCount", { count: advancedFilterCount })}
          </button>
        </div>
        <div className="flex items-center sm:border-b border-rules">
          {showProductComparison && (!isFRM || selectedClient == "BRAFTOVI" || selectedClient == "LORBRENA") && (
            <>
              <Tab tabName={TabNames.DETAIL_VIEW} {...tabs}>
                <VisibilityIcon className="mr-1" /> {t("payerLandscape.detailedView")}
              </Tab>
              <Tab tabName={TabNames.PRODUCT_COMPARISON} {...tabs}>
                <CompareIcon className="mr-1 stroke-current" /> {t("payerLandscape.productComparison")}
              </Tab>
            </>
          )}
          {shouldShowTooltip && (
            <div className="ml-auto flex items-center pb-1">
              <Tooltip placement="bottom-end" tip={tooltipContent}>
                <InfoOutlined className="text-primary" />
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      <TabPanel tabName={TabNames.DETAIL_VIEW} {...tabs}>
        <PayerLandscapeDetailTab key={`detail-tab-${detailFilters.indication}`} />
      </TabPanel>
      <TabPanel tabName={TabNames.PRODUCT_COMPARISON} {...tabs}>
        <PayerLandscapeComparsionTab key="payerLandscapeComparisonTab" />
      </TabPanel>
    </>
  );
};

export default PayerLandscape;
